import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Card, Table, Tag, Typography, Space, Button, message, Descriptions, Switch, Tooltip, Divider, List, Modal, Select, Radio, InputNumber, Input, Form } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import {
  billingService,
  type BillingPlan,
  type SubscriptionInfo,
  type SubscriptionHistoryItem,
  type PaymentOrder,
} from '../services/billing';
import { BILLING_PERMISSIONS } from '../constants/permissions';
import { usePermission } from '../hooks/usePermission';
import { userApi, type User } from '../services/user';
import { useAuthStore } from '../stores/authStore';

const { Title, Paragraph, Text } = Typography;

const currencyFormatter = (value: number, currency: string) => {
  if (!Number.isFinite(value)) return '-';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency,
    minimumFractionDigits: 2,
  }).format(value / 100);
};

const microsToCurrency = (micros: number, currency: string) => {
  if (!Number.isFinite(micros)) return '-';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency,
    minimumFractionDigits: 5,
  }).format(micros / 1_000_000);
};

const Billing: React.FC = () => {
  const [plans, setPlans] = useState<BillingPlan[]>([]);
  const [subscription, setSubscription] = useState<SubscriptionInfo | null>(null);
  const [currentPlan, setCurrentPlan] = useState<BillingPlan | null>(null);
  const [history, setHistory] = useState<SubscriptionHistoryItem[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [loadingHistory, setLoadingHistory] = useState<boolean>(false);
  const [includeInactive, setIncludeInactive] = useState<boolean>(false);
  const [clients, setClients] = useState<User[]>([]);
  const [clientLoading, setClientLoading] = useState<boolean>(false);
  const [selectedClientId, setSelectedClientId] = useState<number | undefined>(undefined);
  const [buyModalVisible, setBuyModalVisible] = useState(false);
  const [buyPlan, setBuyPlan] = useState<BillingPlan | null>(null);
  const [billingCycle, setBillingCycle] = useState<'monthly' | 'yearly'>('monthly');
  const [buyLoading, setBuyLoading] = useState(false);
  const [orders, setOrders] = useState<PaymentOrder[]>([]);
  const [ordersLoading, setOrdersLoading] = useState(false);
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [editingPlan, setEditingPlan] = useState<BillingPlan | null>(null);
  const [editLoading, setEditLoading] = useState(false);
  const [editForm] = Form.useForm();

  const { user } = useAuthStore();
  const canManagePlans = usePermission(BILLING_PERMISSIONS.PLAN_CREATE);
  const canSwitchPlan = usePermission(BILLING_PERMISSIONS.SUBSCRIPTION_UPDATE);
  const isAdmin = user?.user_type === 'admin';

  const loadPlans = useCallback(async () => {
    try {
      setLoading(true);
      const list = await billingService.listPlans(includeInactive);
      setPlans(list);
    } catch (error) {
      console.error('加载套餐失败', error);
      message.error('获取套餐列表失败，请稍后重试');
    } finally {
      setLoading(false);
    }
  }, [includeInactive]);

  const loadSubscription = useCallback(async (clientId?: number) => {
    try {
      const res = await billingService.getCurrentSubscription(clientId);
      setSubscription(res.subscription);
      setCurrentPlan(res.plan ?? null);
    } catch (error) {
      console.error('加载订阅信息失败', error);
      message.error('获取订阅信息失败，请稍后重试');
    }
  }, []);

  const loadHistory = useCallback(async (clientId?: number) => {
    try {
      setLoadingHistory(true);
      const list = await billingService.getSubscriptionHistory(
        clientId ? { client_id: clientId, limit: 20 } : { limit: 20 },
      );
      setHistory(list);
    } catch (error) {
      console.error('加载订阅历史失败', error);
    } finally {
      setLoadingHistory(false);
    }
  }, []);

  const refreshAll = useCallback(
    async (clientId?: number) => {
      const targetClientId = user?.user_type === 'admin' ? (clientId ?? selectedClientId) : undefined;
      await Promise.all([loadPlans(), loadSubscription(targetClientId), loadHistory(targetClientId)]);
    },
    [loadPlans, loadSubscription, loadHistory, selectedClientId, user?.user_type],
  );

  useEffect(() => {
    loadPlans();
  }, [loadPlans]);

  useEffect(() => {
    const targetClientId = user?.user_type === 'admin' ? selectedClientId : undefined;
    loadSubscription(targetClientId);
    loadHistory(targetClientId);
  }, [loadSubscription, loadHistory, selectedClientId, user?.user_type]);

  useEffect(() => {
    const fetchClients = async () => {
      if (user?.user_type !== 'admin') return;
      try {
        setClientLoading(true);
        const response = await userApi.getClientUsers({ page: 1, size: 100 });
        setClients(response.list || []);
        if (!selectedClientId && response.list.length > 0) {
          const firstClient = response.list[0];
          setSelectedClientId(firstClient.client_id || firstClient.id);
          await refreshAll(firstClient.client_id || firstClient.id);
        }
      } catch (error) {
        console.error('加载客户列表失败', error);
        message.error('加载客户列表失败');
      } finally {
        setClientLoading(false);
      }
    };

    fetchClients();
  }, [refreshAll, selectedClientId, user?.user_type]);

  // 加载订单列表
  const loadOrders = useCallback(async () => {
    if (isAdmin) return;
    try {
      setOrdersLoading(true);
      const res = await billingService.listOrders(1, 10);
      setOrders(res.list ?? []);
    } catch (error) {
      console.error('加载订单失败', error);
    } finally {
      setOrdersLoading(false);
    }
  }, [isAdmin]);

  useEffect(() => {
    loadOrders();
  }, [loadOrders]);

  // 检查 URL 参数，支付成功后刷新
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('payment') === 'success') {
      message.success('支付成功，套餐已更新！');
      window.history.replaceState({}, '', window.location.pathname);
      refreshAll();
      loadOrders();
    }
  }, [refreshAll, loadOrders]);

  const activePlanCode = subscription?.plan_code ?? '';

  const renderDailyQuota = useCallback((quota: number) => {
    if (!quota || quota <= 0) {
      return '无限制';
    }
    return `${quota.toLocaleString()} / 日`;
  }, []);

  const planColumns: ColumnsType<BillingPlan> = useMemo(() => {
    return [
      {
        title: '套餐名称',
        dataIndex: 'name',
        render: (text, record) => (
          <Space direction="vertical" size={0}>
            <Space size={6}>
              <Text strong>{record.display_name || text}</Text>
              {record.is_default && <Tag color="gold">默认</Tag>}
            </Space>
            <Text type="secondary" style={{ fontSize: 12 }}>
              代码：{record.plan_code}
            </Text>
          </Space>
        ),
      },
      {
        title: '价格（按月 / 按年）',
        dataIndex: 'monthly_price_cents',
        render: (_, record) => (
          <Space direction="vertical" size={2}>
            <Text>{currencyFormatter(record.monthly_price_cents, record.currency)}</Text>
            <Text type="secondary" style={{ fontSize: 12 }}>
              年付：{currencyFormatter(record.annual_price_cents, record.currency)}
            </Text>
          </Space>
        ),
      },
      {
        title: '每日额度',
        dataIndex: 'daily_quota',
        render: (quota) => <Tag color={quota > 0 ? 'blue' : 'purple'}>{renderDailyQuota(quota)}</Tag>,
      },
      {
        title: '超量计费',
        dataIndex: 'allow_overage',
        render: (_allow, record) => (
          <Space direction="vertical" size={2}>
            <Tag color={record.allow_overage ? 'volcano' : 'default'}>
              {record.allow_overage ? '允许超量' : '不允许超量'}
            </Tag>
            {record.allow_overage && (
              <Text type="secondary" style={{ fontSize: 12 }}>
                {microsToCurrency(record.overage_price_micros, record.currency)} / 次
              </Text>
            )}
          </Space>
        ),
      },
      {
        title: '功能亮点',
        dataIndex: 'features',
        render: (features?: string[]) => (
          <Space wrap size={[8, 8]}>
            {(features ?? []).length === 0 ? (
              <Text type="secondary">无</Text>
            ) : (
              features!.map((feature) => (
                <Tag key={feature} color="cyan">
                  {feature}
                </Tag>
              ))
            )}
          </Space>
        ),
      },
      {
        title: '操作',
        dataIndex: 'actions',
        width: 220,
        render: (_, record) => {
          const isCurrent = record.plan_code === activePlanCode;
          const hasPaidPrice = record.monthly_price_cents > 0 || record.annual_price_cents > 0;
          return (
            <Space>
              {isAdmin ? (
                <>
                  <Tooltip
                    title={
                      !canSwitchPlan && !isCurrent ? '无权限切换套餐' : undefined
                    }
                  >
                    <Button
                      type={isCurrent ? 'default' : 'primary'}
                      disabled={!canSwitchPlan || isCurrent}
                      onClick={() => handleSwitchPlan(record)}
                    >
                      {isCurrent ? '当前套餐' : '切换到此套餐'}
                    </Button>
                  </Tooltip>
                  {canManagePlans && (
                    <Button
                      onClick={() => {
                        setEditingPlan(record);
                        editForm.setFieldsValue({
                          name: record.name,
                          display_name: record.display_name || '',
                          description: record.description || '',
                          monthly_price_dollars: record.monthly_price_cents / 100,
                          annual_price_dollars: record.annual_price_cents / 100,
                          daily_quota: record.daily_quota,
                          features_text: (record.features ?? []).join('\n'),
                          sort_order: record.sort_order,
                        });
                        setEditModalVisible(true);
                      }}
                    >
                      编辑
                    </Button>
                  )}
                </>
              ) : (
                <>
                  {isCurrent ? (
                    <Tag color="green">当前套餐</Tag>
                  ) : hasPaidPrice ? (
                    <Button
                      type="primary"
                      onClick={() => {
                        setBuyPlan(record);
                        setBillingCycle('monthly');
                        setBuyModalVisible(true);
                      }}
                    >
                      购买
                    </Button>
                  ) : (
                    <Tag color="default">免费套餐</Tag>
                  )}
                </>
              )}
              {canManagePlans && (
                <Tag color={record.status === 'active' ? 'success' : 'warning'}>
                  {record.status === 'active' ? '启用' : '停用'}
                </Tag>
              )}
            </Space>
          );
        },
      },
    ];
  }, [activePlanCode, canSwitchPlan, canManagePlans, renderDailyQuota, isAdmin, editForm]);

  // 保存编辑套餐
  const handleSavePlan = async () => {
    if (!editingPlan) return;
    try {
      setEditLoading(true);
      const values = await editForm.validateFields();
      const features = (values.features_text as string || '').split('\n').map((s: string) => s.trim()).filter(Boolean);
      await billingService.updatePlan({
        id: editingPlan.id,
        plan_code: editingPlan.plan_code,
        name: values.name,
        display_name: values.display_name,
        description: values.description,
        currency: editingPlan.currency || 'USD',
        monthly_price_cents: Math.round((values.monthly_price_dollars || 0) * 100),
        annual_price_cents: Math.round((values.annual_price_dollars || 0) * 100),
        daily_quota: values.daily_quota || 0,
        features,
        sort_order: values.sort_order || 0,
        status: editingPlan.status,
        allow_overage: editingPlan.allow_overage,
        overage_price_micros: editingPlan.overage_price_micros,
        is_default: editingPlan.is_default,
      });
      message.success('套餐更新成功');
      setEditModalVisible(false);
      setEditingPlan(null);
      loadPlans();
    } catch (error: any) {
      console.error('更新套餐失败', error);
      message.error(error?.response?.data?.message || '更新套餐失败');
    } finally {
      setEditLoading(false);
    }
  };

  // 购买套餐
  const handleBuyPlan = async () => {
    if (!buyPlan) return;
    try {
      setBuyLoading(true);
      const result = await billingService.createOrder(buyPlan.id, 'monthly');
      setBuyModalVisible(false);
      if (result.invoice_url) {
        message.success('订单已创建，正在跳转支付页面...');
        window.open(result.invoice_url, '_blank');
      }
      loadOrders();
    } catch (error: any) {
      console.error('创建订单失败', error);
      message.error(error?.response?.data?.message || '创建订单失败，请稍后重试');
    } finally {
      setBuyLoading(false);
    }
  };

  const handleSwitchPlan = async (plan: BillingPlan) => {
    const targetClientId = user?.user_type === 'admin' ? selectedClientId : subscription?.client_id;

    if (user?.user_type === 'admin' && !targetClientId) {
      message.warning('请选择需要切换的客户账号');
      return;
    }

    if (!canSwitchPlan) {
      message.warning('当前账号暂无订阅变更权限');
      return;
    }

    Modal.confirm({
      title: `确认切换至「${plan.display_name || plan.name}」套餐？`,
      content: `切换后将立即生效，超量计费与额度将按新套餐执行。`,
      okText: '确认切换',
      cancelText: '取消',
      onOk: async () => {
        try {
          const hide = message.loading('正在切换套餐...');
          await billingService.setSubscriptionPlan({ client_id: targetClientId, plan_id: plan.id });
          hide();
          message.success('套餐切换成功');
          await refreshAll(targetClientId);
        } catch (error) {
          console.error('切换套餐失败', error);
          message.error('切换套餐失败，请稍后重试');
        }
      },
    });
  };

  const HistoryList = useMemo(() => {
    if (history.length === 0) {
      return <Text type="secondary">暂无订阅变更记录</Text>;
    }
    return (
      <List
        size="small"
        loading={loadingHistory}
        dataSource={history}
        renderItem={(item) => (
          <List.Item>
            <Space direction="vertical" size={0} style={{ width: '100%' }}>
              <Text>
                <strong>{item.change_type || '变更'}</strong>
                {item.change_reason ? ` · ${item.change_reason}` : ''}
              </Text>
              <Text type="secondary" style={{ fontSize: 12 }}>
                {item.old_plan_code ? `${item.old_plan_code} → ${item.new_plan_code}` : item.new_plan_code}
              </Text>
              <Text type="secondary" style={{ fontSize: 12 }}>
                {new Date(item.created_at).toLocaleString()}
              </Text>
            </Space>
          </List.Item>
        )}
      />
    );
  }, [history, loadingHistory]);

  // 管理员视图：当前订阅详情
  const AdminCurrentPlanCard = useMemo(() => {
    if (!subscription) {
      return (
        <Card>
          <Space direction="vertical">
            <Title level={4}>当前订阅</Title>
            <Paragraph>当前账号尚未启用任何套餐，请选择合适的套餐开始使用。</Paragraph>
          </Space>
        </Card>
      );
    }

    return (
      <Card>
        <Title level={4}>当前订阅</Title>
        <Descriptions column={1} bordered size="small">
          <Descriptions.Item label="套餐代码">{subscription.plan_code}</Descriptions.Item>
          <Descriptions.Item label="套餐名称">
            <Space size={6}>
              <span>{currentPlan?.display_name || currentPlan?.name || '-'}</span>
              {currentPlan?.is_default && <Tag color="gold">默认套餐</Tag>}
            </Space>
          </Descriptions.Item>
          <Descriptions.Item label="订阅状态">
            <Tag color="processing">{subscription.status}</Tag>
          </Descriptions.Item>
          <Descriptions.Item label="开始时间">
            {new Date(subscription.start_date).toLocaleString()}
          </Descriptions.Item>
          {subscription.trial_end_at && (
            <Descriptions.Item label="试用结束">
              {new Date(subscription.trial_end_at).toLocaleString()}
            </Descriptions.Item>
          )}
          {subscription.end_date && (
            <Descriptions.Item label="订阅截至">
              {new Date(subscription.end_date).toLocaleString()}
            </Descriptions.Item>
          )}
          <Descriptions.Item label="每日额度">
            {subscription.daily_quota > 0
              ? `${subscription.daily_quota.toLocaleString()} 次`
              : '无限制'}
          </Descriptions.Item>
          <Descriptions.Item label="超量策略">
            <Space>
              <Tag color={subscription.allow_overage ? 'volcano' : 'default'}>
                {subscription.allow_overage ? '允许超量' : '禁止超量'}
              </Tag>
              {subscription.allow_overage && currentPlan && (
                <Text type="secondary" style={{ fontSize: 12 }}>
                  {microsToCurrency(subscription.overage_price_micros, currentPlan.currency)} / 次
                </Text>
              )}
            </Space>
          </Descriptions.Item>
          <Descriptions.Item label="自动续费">{subscription.auto_renew ? '是' : '否'}</Descriptions.Item>
        </Descriptions>
      </Card>
    );
  }, [subscription, currentPlan]);

  // 客户视图：简化的当前套餐信息
  const ClientCurrentPlanCard = useMemo(() => {
    return (
      <Card>
        <Title level={4}>我的套餐</Title>
        <Descriptions column={1} bordered size="small">
          <Descriptions.Item label="当前套餐">
            <Text strong>{currentPlan?.display_name || currentPlan?.name || '未开通'}</Text>
          </Descriptions.Item>
          <Descriptions.Item label="状态">
            <Tag color={subscription ? 'green' : 'default'}>{subscription ? '已开通' : '未开通'}</Tag>
          </Descriptions.Item>
          {subscription && (
            <Descriptions.Item label="每日额度">
              {subscription.daily_quota > 0
                ? `${subscription.daily_quota.toLocaleString()} 次`
                : '无限制'}
            </Descriptions.Item>
          )}
        </Descriptions>
      </Card>
    );
  }, [subscription, currentPlan]);

  // ========== 客户视图 ==========
  if (!isAdmin) {
    const isFreePlan = !currentPlan || currentPlan.is_default || currentPlan.plan_code === 'free';
    const isPaid = !isFreePlan;
    const STATUS_CONFIG: Record<string, { label: string; color: string }> = {
      pending: { label: '准备中', color: 'default' },
      waiting: { label: '待支付', color: 'warning' },
      confirming: { label: '确认中', color: 'processing' },
      confirmed: { label: '已确认', color: 'success' },
      sending: { label: '处理中', color: 'processing' },
      finished: { label: '已完成', color: 'success' },
      failed: { label: '失败', color: 'error' },
      expired: { label: '已过期', color: 'default' },
      refunded: { label: '已退款', color: 'warning' },
    };

    return (
      <Space direction="vertical" size={24} style={{ width: '100%' }}>
        {/* 页面标题 */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <Title level={3} style={{ margin: 0 }}>账单与订阅</Title>
            <Text type="secondary">管理你的套餐和查看订单记录</Text>
          </div>
          <Button icon={<span>↻</span>} onClick={() => { refreshAll(); loadOrders(); }}>
            刷新
          </Button>
        </div>

        {/* 当前套餐卡片 */}
        <Card
          style={{
            background: isPaid
              ? 'linear-gradient(135deg, #1890ff 0%, #722ed1 100%)'
              : '#f5f5f5',
            borderRadius: 16,
            border: 'none',
          }}
          styles={{ body: { padding: 24 } }}
        >
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
              <div style={{
                width: 48, height: 48, borderRadius: 12, display: 'flex', alignItems: 'center', justifyContent: 'center',
                background: isPaid ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.06)',
                fontSize: 24,
              }}>
                📦
              </div>
              <div>
                <Title level={4} style={{ margin: 0, color: isPaid ? '#fff' : undefined }}>
                  {currentPlan?.display_name || currentPlan?.name || '免费版'}
                </Title>
                <Text style={{ color: isPaid ? 'rgba(255,255,255,0.8)' : undefined }}>
                  {isPaid ? '当前订阅' : '免费版 · 基础功能'}
                </Text>
              </div>
            </div>
            <Button
              type={isPaid ? 'default' : 'primary'}
              style={isPaid ? { background: 'rgba(255,255,255,0.2)', color: '#fff', border: 'none' } : {}}
              onClick={() => {
                const el = document.getElementById('plan-section');
                el?.scrollIntoView({ behavior: 'smooth' });
              }}
            >
              {isPaid ? '更换套餐' : '升级套餐'} →
            </Button>
          </div>

          {/* 额度指标网格 */}
          {subscription && (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 12, marginTop: 20 }}>
              <div style={{
                background: isPaid ? 'rgba(255,255,255,0.1)' : '#fff',
                borderRadius: 12, padding: '12px 16px',
              }}>
                <Text style={{ fontSize: 12, color: isPaid ? 'rgba(255,255,255,0.6)' : '#999' }}>每日额度</Text>
                <div>
                  <Text strong style={{ fontSize: 20, color: isPaid ? '#fff' : undefined }}>
                    {subscription.daily_quota > 0 ? subscription.daily_quota.toLocaleString() : '无限'}
                  </Text>
                </div>
              </div>
              <div style={{
                background: isPaid ? 'rgba(255,255,255,0.1)' : '#fff',
                borderRadius: 12, padding: '12px 16px',
              }}>
                <Text style={{ fontSize: 12, color: isPaid ? 'rgba(255,255,255,0.6)' : '#999' }}>订阅状态</Text>
                <div>
                  <Text strong style={{ fontSize: 20, color: isPaid ? '#fff' : undefined }}>
                    {subscription.status === 'active' ? '生效中' : subscription.status}
                  </Text>
                </div>
              </div>
            </div>
          )}
        </Card>

        {/* 套餐选择区域 */}
        <div id="plan-section">
          <Card>
            <div style={{ textAlign: 'center', marginBottom: 24 }}>
              <Title level={4} style={{ marginBottom: 4 }}>选择套餐</Title>
              <Text type="secondary">按需升级，支持 USDT 支付</Text>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: 16 }}>
              {plans.filter(p => p.status === 'active' && p.plan_code !== 'unlimited').map((plan) => {
                const isCurrent = plan.plan_code === activePlanCode;
                const hasPaidPrice = plan.monthly_price_cents > 0;
                const displayPrice = plan.monthly_price_cents;

                return (
                  <Card
                    key={plan.id}
                    hoverable
                    style={{
                      borderRadius: 16,
                      borderColor: isCurrent ? '#52c41a' : undefined,
                      borderWidth: isCurrent ? 2 : 1,
                    }}
                    styles={{ body: { padding: 24, display: 'flex', flexDirection: 'column', minHeight: 340 } }}
                  >
                    <div style={{ flex: 1 }}>
                      <div style={{ marginBottom: 12 }}>
                        <Text strong style={{ fontSize: 18 }}>{plan.display_name || plan.name}</Text>
                        {isCurrent && <Tag color="green" style={{ marginLeft: 8 }}>当前</Tag>}
                      </div>

                      <div style={{ marginBottom: 4 }}>
                        <Text style={{ fontSize: 32, fontWeight: 700, color: '#1890ff' }}>
                          {hasPaidPrice ? currencyFormatter(displayPrice, plan.currency) : '免费'}
                        </Text>
                        {hasPaidPrice && (
                          <Text type="secondary" style={{ fontSize: 13 }}>/月</Text>
                        )}
                      </div>
                      {!hasPaidPrice && <Text style={{ fontSize: 12, color: '#52c41a' }}>免费试用</Text>}

                      <Divider style={{ margin: '16px 0 12px' }} />

                      <Space direction="vertical" size={6} style={{ width: '100%' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <span style={{ color: '#52c41a' }}>✓</span>
                          <Text style={{ fontSize: 13 }}>
                            每日 {plan.daily_quota > 0 ? `${plan.daily_quota.toLocaleString()} 次` : '无限'}访问
                          </Text>
                        </div>
                        {(plan.features ?? []).map((f) => (
                          <div key={f} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            <span style={{ color: '#52c41a' }}>✓</span>
                            <Text style={{ fontSize: 13 }}>{f}</Text>
                          </div>
                        ))}
                      </Space>
                    </div>

                    <div style={{ marginTop: 20 }}>
                      {isCurrent ? (
                        <Button block disabled style={{ borderRadius: 10, height: 42 }}>当前套餐</Button>
                      ) : hasPaidPrice ? (
                        <Button
                          block
                          type="primary"
                          style={{ borderRadius: 10, height: 42, fontWeight: 600 }}
                          onClick={() => {
                            setBuyPlan(plan);
                            setBuyModalVisible(true);
                          }}
                        >
                          USDT 支付升级
                        </Button>
                      ) : (
                        <Button block disabled style={{ borderRadius: 10, height: 42 }}>免费版</Button>
                      )}
                    </div>
                  </Card>
                );
              })}
            </div>
          </Card>
        </div>

        {/* 订单记录 */}
        <Card
          title={
            <Space>
              <span>💳</span>
              <Text strong>订单记录</Text>
              <Text type="secondary" style={{ fontSize: 12, fontWeight: 'normal' }}>{orders.length} 条记录</Text>
            </Space>
          }
        >
          {ordersLoading ? (
            <div style={{ textAlign: 'center', padding: 48 }}>
              <Text type="secondary">加载中...</Text>
            </div>
          ) : orders.length === 0 ? (
            <div style={{ textAlign: 'center', padding: 48 }}>
              <div style={{ fontSize: 32, marginBottom: 12 }}>💳</div>
              <Text type="secondary">暂无订单记录</Text>
              <div style={{ marginTop: 8 }}>
                <Button type="link" onClick={() => {
                  const el = document.getElementById('plan-section');
                  el?.scrollIntoView({ behavior: 'smooth' });
                }}>前往升级套餐</Button>
              </div>
            </div>
          ) : (
            <Space direction="vertical" size={0} style={{ width: '100%' }}>
              {orders.map((order) => {
                const st = STATUS_CONFIG[order.status] || STATUS_CONFIG.pending;
                const matchedPlan = plans.find(p => p.id === order.plan_id);
                return (
                  <div
                    key={order.id}
                    style={{
                      padding: '16px 0',
                      borderBottom: '1px solid #f0f0f0',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                      <div style={{
                        width: 36, height: 36, borderRadius: 10,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        background: order.status === 'finished' || order.status === 'confirmed'
                          ? '#f6ffed' : ['failed', 'expired'].includes(order.status) ? '#fff2f0' : '#fffbe6',
                        fontSize: 16,
                      }}>
                        {order.status === 'finished' || order.status === 'confirmed' ? '✅' :
                          ['failed', 'expired'].includes(order.status) ? '❌' : '⏳'}
                      </div>
                      <div>
                        <Text strong style={{ fontSize: 14 }}>
                          {matchedPlan?.display_name || matchedPlan?.name || '套餐'}
                          {' · '}{order.billing_cycle === 'yearly' ? '年付' : '月付'}
                        </Text>
                        <div>
                          <Text type="secondary" style={{ fontSize: 11, fontFamily: 'monospace' }}>{order.id}</Text>
                        </div>
                      </div>
                    </div>
                    <div style={{ textAlign: 'right' }}>
                      <Text strong>${order.amount.toFixed(2)}</Text>
                      <div>
                        <Tag color={st.color as any} style={{ marginRight: 0 }}>{st.label}</Tag>
                      </div>
                    </div>
                    <div>
                      <Text type="secondary" style={{ fontSize: 11 }}>
                        {new Date(order.created_at).toLocaleDateString('zh-CN', {
                          month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit',
                        })}
                      </Text>
                      {order.invoice_url && ['pending', 'waiting'].includes(order.status) && (
                        <div style={{ marginTop: 4 }}>
                          <Button
                            size="small"
                            type="primary"
                            style={{ borderRadius: 8, fontSize: 12 }}
                            onClick={() => window.open(order.invoice_url, '_blank')}
                          >
                            继续支付 →
                          </Button>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </Space>
          )}
        </Card>

        {/* 购买确认弹窗 */}
        <Modal
          title={`购买 ${buyPlan?.display_name || buyPlan?.name || ''}`}
          open={buyModalVisible}
          onCancel={() => setBuyModalVisible(false)}
          onOk={handleBuyPlan}
          confirmLoading={buyLoading}
          okText="确认购买 (USDT)"
          cancelText="取消"
          centered
        >
          {buyPlan && (
            <Space direction="vertical" size={16} style={{ width: '100%' }}>
              <Card style={{ background: '#f6f8fa', border: 'none' }}>
                <div style={{ textAlign: 'center' }}>
                  <Text type="secondary" style={{ fontSize: 12 }}>月付金额</Text>
                  <div>
                    <Text strong style={{ fontSize: 28, color: '#1890ff' }}>
                      {currencyFormatter(buyPlan.monthly_price_cents, buyPlan.currency)}
                    </Text>
                  </div>
                  <Tag color="green" style={{ marginTop: 8 }}>USDT (TRC20)</Tag>
                </div>
              </Card>
            </Space>
          )}
        </Modal>
      </Space>
    );
  }

  // ========== 管理员视图 ==========
  return (
    <Space direction="vertical" size={24} style={{ width: '100%' }}>
      <Card>
        <Space direction="vertical" size={12} style={{ width: '100%' }}>
          <Title level={3}>计费中心</Title>
          <Paragraph type="secondary">
            管理访问套餐、查看当前订阅与变更历史。套餐支持最低消费、按量计费与高级功能差异，确保业务按需扩展。
          </Paragraph>
          <Space size={12} wrap>
            <Text>选择客户</Text>
            <Select
              style={{ minWidth: 240 }}
              loading={clientLoading}
              placeholder="请选择客户账号"
              value={selectedClientId}
              onChange={(value) => {
                setSelectedClientId(value);
                refreshAll(value);
              }}
              options={clients.map((client) => ({
                label: `${client.username} (${client.email || '未填写邮箱'})`,
                value: client.client_id || client.id,
              }))}
            />
          </Space>
          {canManagePlans && (
            <Space>
              <Text>显示已停用套餐</Text>
              <Switch checked={includeInactive} onChange={(checked) => setIncludeInactive(checked)} />
            </Space>
          )}
          <Space>
            <Button
              type="primary"
              onClick={() => refreshAll(selectedClientId)}
              loading={loading || loadingHistory}
            >
              刷新数据
            </Button>
          </Space>
        </Space>
      </Card>

      {AdminCurrentPlanCard}

      <Card>
        <Space direction="vertical" size={16} style={{ width: '100%' }}>
          <Space align="center" style={{ justifyContent: 'space-between', width: '100%' }}>
            <Title level={4} style={{ margin: 0 }}>
              套餐列表
            </Title>
            <Text type="secondary">请选择合适的套餐，支持随时升级或降级</Text>
          </Space>
          <Table
            rowKey="id"
            loading={loading}
            columns={planColumns}
            dataSource={plans}
            pagination={false}
            bordered
          />
        </Space>
      </Card>

      <Card>
        <Title level={4}>订阅变更记录</Title>
        <Divider style={{ margin: '12px 0' }} />
        {HistoryList}
      </Card>

      {/* 编辑套餐弹窗 */}
      <Modal
        title={`编辑套餐 - ${editingPlan?.display_name || editingPlan?.name || ''}`}
        open={editModalVisible}
        onCancel={() => { setEditModalVisible(false); setEditingPlan(null); }}
        onOk={handleSavePlan}
        confirmLoading={editLoading}
        okText="保存"
        cancelText="取消"
        width={520}
        destroyOnClose
      >
        <Form form={editForm} layout="vertical" style={{ marginTop: 16 }}>
          <Form.Item label="套餐名称" name="name" rules={[{ required: true, message: '请输入套餐名称' }]}>
            <Input />
          </Form.Item>
          <Form.Item label="显示名称" name="display_name">
            <Input placeholder="对客户展示的名称" />
          </Form.Item>
          <Form.Item label="套餐描述" name="description">
            <Input.TextArea rows={2} />
          </Form.Item>
          <Space size={16}>
            <Form.Item label="月付价格 ($)" name="monthly_price_dollars">
              <InputNumber min={0} step={0.01} precision={2} style={{ width: 150 }} />
            </Form.Item>
            <Form.Item label="年付价格 ($)" name="annual_price_dollars">
              <InputNumber min={0} step={0.01} precision={2} style={{ width: 150 }} />
            </Form.Item>
          </Space>
          <Form.Item label="每日额度（0 = 无限制）" name="daily_quota">
            <InputNumber min={0} style={{ width: 200 }} />
          </Form.Item>
          <Form.Item label="功能亮点（每行一个）" name="features_text">
            <Input.TextArea rows={4} placeholder={'每日 200 次访问\n基础安全防护\n30天有效'} />
          </Form.Item>
          <Form.Item label="排序权重" name="sort_order">
            <InputNumber min={0} style={{ width: 120 }} />
          </Form.Item>
        </Form>
      </Modal>
    </Space>
  );
};

export default Billing;

